import React, { useEffect, useState } from "react";
import { Box, Button, CircularProgress, Paper, Typography } from "@mui/material";
import {
  ArrowBack as ArrowBackIcon,
  RocketLaunch as RocketLaunchIcon,
} from "@mui/icons-material";
import { Navigate, useLocation, useNavigate, useParams } from "react-router-dom";
import { useAuth } from "context/AuthContext";
import { getUserApplications, isAdminUser } from "modules/systems/services/application.service";
import "./ApplicationDevelopmentPage.css";

const ApplicationDevelopmentPage = ({ title = "Ứng dụng đang được phát triển" }) => {
  const { user } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const { applicationId } = useParams();
  const application = location.state?.application;
  const [checkingAccess, setCheckingAccess] = useState(!!applicationId && !application);
  const [hasAccess, setHasAccess] = useState(true);
  const [resolvedApplication, setResolvedApplication] = useState(application);

  useEffect(() => {
    let isMounted = true;

    const checkAccess = async () => {
      if (!applicationId || application || isAdminUser(user)) {
        setCheckingAccess(false);
        setHasAccess(true);
        return;
      }

      if (!user?.userId) {
        setCheckingAccess(false);
        setHasAccess(false);
        return;
      }

      try {
        const applications = await getUserApplications(user.userId);
        const matchedApplication = applications.find(
          (item) => String(item.applicationId) === String(applicationId)
        );

        if (isMounted) {
          setResolvedApplication(matchedApplication);
          setHasAccess(!!matchedApplication);
        }
      } catch (error) {
        if (isMounted) {
          setHasAccess(false);
        }
      } finally {
        if (isMounted) {
          setCheckingAccess(false);
        }
      }
    };

    checkAccess();

    return () => {
      isMounted = false;
    };
  }, [application, applicationId, user]);

  const applicationDescription =
    resolvedApplication?.description ||
    resolvedApplication?.Description ||
    (applicationId ? `Ứng dụng #${applicationId}` : title);

  if (checkingAccess) {
    return (
      <Box className="development-page">
        <Box display="flex" alignItems="center" flexDirection="column" gap={2}>
          <CircularProgress />
          <Typography color="text.secondary">
            Đang kiểm tra quyền truy cập ứng dụng...
          </Typography>
        </Box>
      </Box>
    );
  }

  if (!hasAccess) {
    return <Navigate to="/portal" replace />;
  }

  return (
    <Box className="development-page">
      <Paper elevation={0} className="development-panel">
        <Box className="development-visual" aria-hidden="true">
          <Box className="development-rocket">
            <RocketLaunchIcon />
          </Box>
          <Box className="development-cloud development-cloud-main" />
          <Box className="development-cloud development-cloud-side" />
        </Box>

        <Box className="development-content">
          <Typography className="development-eyebrow" variant="overline">
            Đang phát triển
          </Typography>
          <Typography variant="h4" className="development-title">
            Ứng dụng {applicationDescription} sẽ sớm ra mắt
          </Typography>
          <Typography variant="body1" className="development-description">
            Chúng tôi đang hoàn thiện chức năng này để mang lại trải nghiệm tốt hơn. Vui lòng quay lại trong các phiên bản tiếp theo.
          </Typography>
          <Button
            variant="contained"
            startIcon={<ArrowBackIcon />}
            onClick={() => navigate("/portal")}
            className="development-back-button"
          >
            Quay lại
          </Button>
        </Box>
      </Paper>
    </Box>
  );
};

export default ApplicationDevelopmentPage;
